// src/components/intake/IntakeDashboard.jsx
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext.jsx";
import { Button, Pass } from "../ui";
import Loader from "../Loader";
import DashboardHeader from "../dashboard-flow/DashboardHeader";
import { getIntake } from "../../shared/_services/intake.services";
import { LOAD_FAILED, TRY_AGAIN } from "../onboarding/onboardingCopy";
import AnswerView from "./AnswerView";
import { intakePath, questionsEditPath } from "./intakeNav";
import {
  CARD_EDIT,
  CARD_EDIT_NAMED,
  CARD_TITLES,
  DASH_ANSWERS_HEADING,
  DASH_H1,
  PASS_BIG,
  PASS_KIND,
  PASS_PICK_UP,
  PASS_PROGRESS,
  PASS_PROGRESS_LABEL,
  PASS_START,
  PASS_SUB,
} from "./intakeCopy";

// /dashboard while the stage is intake (spec § Screens, decisions 4, 5, 17):
// the no-data layout's "Your questions" pass — the count from the stage's
// intakePageCount, progress from `answered` (never `completed`: a skipped page
// is not answered, Review Focus 1), Start the questions / Pick up where you
// left off → the wizard at nextPageKey — plus an answer card for every
// question that has an answer, each linking to its Questions page item.
// With the vault down the pass shows its button without the count.
export default function IntakeDashboard({ stage }) {
  const { user } = useAuth();
  const [load, setLoad] = useState({ status: "loading", data: null });
  // A failed read keeps what was already known (the last good payload).
  const read = useCallback(async () => {
    setLoad((l) => ({ ...l, status: "loading" }));
    try {
      const res = await getIntake();
      setLoad({ status: "ready", data: res.data });
    } catch (_) {
      setLoad((l) => ({ ...l, status: "error" }));
    }
  }, []);
  useEffect(() => {
    read();
  }, [read]);

  const count = stage?.intakePageCount;
  const data = load.data;
  const cards =
    data?.vault === "ok"
      ? data.pages.filter((p) => data.answered.includes(p.key))
      : [];

  return (
    <>
      <DashboardHeader />
      <div className="tw-pb-16 tw-font-sans tw-text-text">
        <h1 className="tw-m-0 tw-text-2xl tw-font-bold">
          {DASH_H1(user?.firstName)}
        </h1>
        {load.status === "loading" && !data && <Loader />}
        {load.status === "error" && (
          <div role="alert" className="tw-mt-6">
            <span>{LOAD_FAILED}</span>{" "}
            <Button variant="text" onClick={read}>
              {TRY_AGAIN}
            </Button>
          </div>
        )}
        <Pass
          kind={PASS_KIND}
          className="tw-mt-6 tw-max-w-xl"
          stub={
            <>
              {data?.vault === "ok" && (
                <dl className="tw-m-0">
                  <dt className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-widest tw-text-text-muted">
                    {PASS_PROGRESS_LABEL}
                  </dt>
                  <dd className="tw-m-0 tw-text-sm tw-font-semibold">
                    {PASS_PROGRESS(data.answered.length, count)}
                  </dd>
                </dl>
              )}
              {!data && <Button disabled>{PASS_START}</Button>}
              {data?.nextPageKey && (
                <Button as={Link} to={intakePath(data.nextPageKey)}>
                  {data.completed.length ? PASS_PICK_UP : PASS_START}
                </Button>
              )}
            </>
          }
        >
          <p className="tw-m-0 tw-text-xl tw-font-bold">{PASS_BIG}</p>
          <p className="tw-m-0 tw-text-sm tw-text-text-muted">
            {PASS_SUB(count)}
          </p>
        </Pass>
        {cards.length > 0 && (
          <section aria-labelledby="dash-answers" className="tw-mt-8">
            <h2 id="dash-answers" className="tw-m-0 tw-text-lg tw-font-bold">
              {DASH_ANSWERS_HEADING}
            </h2>
            <ul className="tw-m-0 tw-mt-4 tw-grid tw-list-none tw-grid-cols-1 tw-gap-6 tw-p-0 lg:tw-grid-cols-2">
              {cards.map((page) => (
                <li
                  key={page.key}
                  className="tw-rounded-lg tw-border tw-border-solid tw-border-divider tw-bg-surface tw-p-6"
                >
                  <div className="tw-mb-4 tw-flex tw-items-baseline tw-justify-between tw-gap-3">
                    <h3 className="tw-m-0 tw-text-lg tw-font-bold">
                      {CARD_TITLES[page.key]}
                    </h3>
                    <Link
                      to={questionsEditPath(page.key)}
                      aria-label={CARD_EDIT_NAMED(CARD_TITLES[page.key])}
                      className="tw-text-sm tw-font-semibold tw-text-link hover:tw-text-link-hover"
                    >
                      {CARD_EDIT}
                    </Link>
                  </div>
                  <AnswerView page={page} answer={data.answers[page.key]} />
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </>
  );
}
